/**
 * Helpers de dominio/subdominio (smarty.lat = marketing, admin.smarty.lat = panel, {tenant}.smarty.lat = tienda).
 * Single source of truth para App, DomainRouter, FeatureProtectedRoute, ecommerce-urls.
 */

import {
  WEB_HOST_STAGING,
  ROOT_DOMAIN_PROD,
  ROOT_DOMAIN_DEV,
  STOREFRONT_ROOT_DOMAIN,
  ADMIN_HOST_PROD,
  ADMIN_HOST_DEV,
  WEB_HOST_PROD,
  WEB_HOST_DEV,
  isReservedSubdomain,
} from '@/config/app-domains.config';

export function getHostname(): string {
  return typeof window !== 'undefined' ? window.location.hostname : '';
}

function stripPort(h: string): string {
  return h.split(':')[0];
}

/** Dominio raíz (marketing): smarty.lat, www.smarty.lat, smarty.local, www.smarty.local */
export function isMarketingDomain(hostname: string = getHostname()): boolean {
  const h = stripPort(hostname);
  return (
    h === ROOT_DOMAIN_PROD ||
    h === `www.${ROOT_DOMAIN_PROD}` ||
    h === ROOT_DOMAIN_DEV ||
    h === `www.${ROOT_DOMAIN_DEV}`
  );
}

/** Solo super admin: admin.smarty.lat / admin.smarty.local */
export function isAdminDomain(hostname: string = getHostname()): boolean {
  const h = stripPort(hostname);
  return h === ADMIN_HOST_PROD || h === ADMIN_HOST_DEV;
}

/**
 * Host efímero de una vista previa: `algo.pages.dev` (Cloudflare) o `algo.vercel.app` (Vercel).
 *
 * POR QUÉ EXISTE: `DomainRouter` manda a la landing de marketing cualquier host que no reconozca, y
 * las URLs de vista previa nunca estuvieron en la lista. O sea que **una vista previa del panel
 * jamás mostró el panel** — en NINGUNA plataforma: se abría, se veía la landing, y su botón
 * "Iniciar sesión" llevaba a `web.smarty.lat`, o sea a PRODUCCIÓN. Quien creyera estar probando un
 * cambio estaba mirando la app en vivo.
 *
 * Se descubrió publicando el panel en Cloudflare Pages, pero no es de Cloudflare: los despliegues de
 * vista previa de Vercel venían con el mismo defecto desde siempre, gastando cupo para producir URLs
 * que no servían para lo único que se querían.
 *
 * 🪤 SÓLO ABRE EL PANEL, NO EL SUPER ADMIN. Una vista previa se comporta como `web.smarty.lat`, que
 * exige iniciar sesión como cualquier usuario. No hereda `admin.smarty.lat`: ese host da la consola
 * de super administrador y no tiene por qué vivir en una URL efímera que se comparte por chat.
 */
function esVistaPrevia(h: string): boolean {
  return h.endsWith('.pages.dev') || h.endsWith('.vercel.app');
}

/** Panel de mercaderes (dashboard, POS, login): web.smarty.lat / web.smarty.local */
export function isWebDomain(hostname: string = getHostname()): boolean {
  const h = stripPort(hostname);
  // 🚨 `staging.smarty.lat` ES un panel. Sin esta línea, su raíz caía en la landing de marketing
  //    —el founder entró y vio «Gestiona tu restaurante como un profesional»— en vez del login.
  return h === WEB_HOST_PROD || h === WEB_HOST_DEV || h === WEB_HOST_STAGING || esVistaPrevia(h);
}

/** Panel de producto (dashboard/POS): web.smarty.lat (mercaderes) */
export function isAppDomain(hostname: string = getHostname()): boolean {
  return isWebDomain(hostname);
}

export function isLocalhost(hostname: string = getHostname()): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

/**
 * Subdominio de tenant para ecommerce: *.smarty.lat / *.smarty.local.
 * Retorna null para admin, www, raíz (smarty.lat) o si no aplica.
 */
export function getTenantSubdomain(hostname: string = getHostname()): string | null {
  const h = stripPort(hostname);
  if (!h.endsWith(`.${ROOT_DOMAIN_PROD}`) && !h.endsWith(`.${ROOT_DOMAIN_DEV}`)) return null;
  const sub = h.split('.')[0];
  if (!sub) return null;
  if (isReservedSubdomain(sub)) return null;
  if (h === ROOT_DOMAIN_PROD || h === ROOT_DOMAIN_DEV) return null;
  return sub;
}

/**
 * URLs antiguas dadas de baja: *.food.smarty.lat y *.food.smarty.local
 * (app.food.smarty.lat, admin.food.smarty.lat, dulti.food.smarty.lat, etc.).
 * Si el host coincide, se debe mostrar la página de "URL no válida" en lugar de marketing.
 */
export function isDeprecatedLegacyDomain(hostname: string = getHostname()): boolean {
  const h = stripPort(hostname);
  return h.includes('.food.smarty.lat') || h.includes('.food.smarty.local');
}

/**
 * URL del storefront PÚBLICO de un tenant: `https://{slug}.smartylat.com{path}`.
 * Sirve para el botón "Vista previa" estilo Shopify (ver el producto en la tienda).
 *
 * Usa el DOMINIO DE TIENDAS dedicado `smartylat.com` (modelo myshopify), NO el
 * corporativo `smarty.lat`. SIEMPRE producción: el storefront es el Cloudflare Worker
 * en prod (con SSL válido); en dev local no hay storefront. Los tenants legacy
 * (dulti/vitalex) también resuelven en `.smartylat.com` vía su slug en el KV del worker.
 */
export function tenantStorefrontUrl(tenantSlug: string, path = '/'): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  return `https://${encodeURIComponent(tenantSlug)}.${STOREFRONT_ROOT_DOMAIN}${p}`;
}
